import {
  CreateLocationDto,
  LocationDto,
  UpdateLocationDto,
} from "@braoshzmvavz/picnube-common";
import { useMantineTheme } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  FillLayerSpecification,
  GeoJSONSource,
  MapLibreEvent,
} from "maplibre-gl";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { HiChevronRight, HiPlus, HiX } from "react-icons/hi";
import Map, {
  FullscreenControl,
  GeolocateControl,
  Layer,
  MapLayerMouseEvent,
  MapRef,
  Marker,
  NavigationControl,
  ScaleControl,
  Source,
  ViewStateChangeEvent,
} from "react-map-gl/maplibre";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import useApi from "../../services/useApi";
import {
  addLocation,
  updateLocationState,
} from "../../state/locations/locationSlice";
import { clusterLayer } from "./layers";
import LocationEditForm from "./LocationEditForm";
import LocationInfo from "./LocationInfo";
import MapOverlay from "./MapOverlay";
import MapOverlayButton from "./MapOverlayButton";
import { MapViewMode } from "./MapView";
import Pin from "./Pin";

// TODO: Animate a point along a path
// TODO: https://maplibre.org/maplibre-gl-js/docs/examples/animate-point-along-route/
export default function BaseMap(props: {
  defaultMode: MapViewMode;
  defaultSelectedLocation?: string;
  selectedLocation: LocationDto | null;
  onSelectLocation: (location: LocationDto) => void;
  onSelectLocationForDetail: (location: LocationDto) => void;
  projection?: 'globe' | 'mercator' | undefined;
  onConfirm: (location: LocationDto) => void;
}) {
  const mapStyleJsons = {
    positron: "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json",
    dark: "https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json",
    voyager: "https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json",
    openStreet:
      "https://raw.githubusercontent.com/go2garret/maps/main/src/assets/json/openStreetMap.json",
    icgcDark:
      "https://geoserveis.icgc.cat/contextmaps/icgc_mapa_base_fosc.json",
  };

  const theme = useMantineTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);

  const dispatch = useAppDispatch();
  const locationState = useAppSelector((state) => state.location);

  const { createLocation, updateLocation } = useApi();

  const mapRef = useRef<MapRef>(null);
  const [mapCenter, setMapCenter] = useState([0, 0]);

  const [countriesGeoJSON, setCountriesGeoJSON] = useState<any>({});

  const [isAddLocationMode, setIsAddLocationMode] = useState(false);
  const [isEditLocationMode, setIsEditLocationMode] = useState(false);

  const [defaultLocationLatLong, setDefaultLocationLatLong] = useState([
    9.389839, 50.622884,
  ]);

  const [hoveredStateId, setHoveredStateId] = useState<string | null>(null);
  const [selectedCountryCode, setSelectedCountryCode] = useState<string | null>(
    null
  );
  const [featureIdByCountry, setFeatureIdByCountry] = useState<
    Record<string, string>
  >({});
  const [zoom, setZoom] = useState(3);

  const pins: React.JSX.Element[] = useMemo(
    () =>
      locationState.locations
        .filter((location) => location != null)
        .filter(
          (location) =>
            location.countryCode === selectedCountryCode || zoom >= 4
        )
        .map((mapPoint, index) => (
          <Marker
            key={`marker-${index}`}
            longitude={mapPoint.longitude}
            latitude={mapPoint.latitude}
            anchor="bottom"
            onClick={(e) => {
              // If we let the click event propagates to the map, it will immediately close the popup
              // with `closeOnClick: true`
              e.originalEvent.stopPropagation();

              if (
                !isAddLocationMode &&
                (!isEditLocationMode ||
                  props.selectedLocation?.id == mapPoint?.id)
              ) {
                onSelectLocation(mapPoint);
              }
            }}
          >
            <Pin
              selected={
                !(isAddLocationMode || isEditLocationMode) &&
                mapPoint?.name === props.selectedLocation?.name
              }
              light={
                isEditLocationMode &&
                mapPoint?.id === props.selectedLocation?.id
              }
              color="gray"
              mapPoint={mapPoint}
            />
          </Marker>
        )),
    [
      props.selectedLocation,
      isAddLocationMode,
      isEditLocationMode,
      locationState.locations,
      selectedCountryCode,
      zoom,
    ]
  );

  useEffect(() => {
    import("../../assets/countries.geo.json").then((module) => {
      const countriesRaw = module.default;
      setCountriesGeoJSON({
        ...countriesRaw,
        features: countriesRaw["features"].map((feature, index) => ({
          ...feature,
          id: index, // or use a hash of a property like `feature.properties.name`
        })),
      });
    });
  }, []);

  // On map load, zoom to the country with the most locations
  useEffect(() => {
    if (!mapRef?.current || !mapRef.current.isStyleLoaded()) return;
    setZoom(3.1);
  }, [mapRef.current]);

  const updateHighlightedCountries = () => {
    countriesGeoJSON.features.forEach((feature) => {
      if (
        locationState.locations.find(
          (location) =>
            location.countryCode === feature.properties["ISO3166-1-Alpha-2"]
        ) &&
        zoom < 4
      ) {
        mapRef.current.setFeatureState(
          {
            source: "countries",
            id: feature.id,
          },
          { hasLocations: true }
        );
      } else {
        mapRef.current.setFeatureState(
          {
            source: "countries",
            id: feature.id,
          },
          { hasLocations: false }
        );
      }
    });
  };

  // Highlight countries with locations
  useEffect(() => {
    if (
      !mapRef?.current ||
      !countriesGeoJSON?.features ||
      !mapRef.current.isStyleLoaded()
    )
      return;
    updateHighlightedCountries();
  }, [locationState.locations, zoom]);

  const flyToSelectedLocation = () => {
    if (!props.selectedLocation) return;
    const { latitude, longitude } = props.selectedLocation;

    mapRef.current?.flyTo({
      center: [longitude, latitude],
      zoom: 5,
      duration: 1500,
    });
  };

  useEffect(() => {
    if (!props.selectedLocation) return;
    flyToSelectedLocation();
  }, [props.selectedLocation]);

  const onMapViewportChange = () => {
    const map = mapRef.current;
    if (!map) return;

    const { lat, lng } = map.getCenter();
    setMapCenter([lat, lng]);
  };

  const onSelectLocation = (location: LocationDto) => {
    flyToSelectedLocation();
    props.onSelectLocation(location);
  };

  const onSubmit = (location: LocationDto) => {
    const locationName = location.name.trim();

    if (!locationName || locationName.length < 2) {
      notifications.show({
        id: "error-empty-location-name",
        color: "red",
        title: "Error",
        message: "Location name should be at least 2 characters",
        autoClose: false,
      });
      return;
    }

    if (isAddLocationMode) {
      // Create new location
      const location: CreateLocationDto = {
        name: locationName,
        latitude: mapCenter[0],
        longitude: mapCenter[1],
      };

      createLocation(location)
        .then((response: LocationDto) => {
          console.log("createLocation", response);
          setIsAddLocationMode(false);
          dispatch(addLocation(response));
          if (response.id === props.selectedLocation?.id) {
            onSelectLocation(response);
          }
          props.onConfirm(response);
        })
        .catch((err) => {
          // TODO: show error
          console.error("createLocation", err);
        });
    } else if (isEditLocationMode) {
      const location: UpdateLocationDto = {
        id: props.selectedLocation.id,
        name: locationName,
        latitude: mapCenter[0],
        longitude: mapCenter[1],
      };

      console.log("isEditLocationMode", location);

      updateLocation(location)
        .then((response: LocationDto) => {
          console.log("updateLocation", response);
          setIsEditLocationMode(false);
          dispatch(updateLocationState(response));
          onSelectLocation(response);
          props.onConfirm(response);
        })
        .catch((err) => {
          // TODO: show error
          console.error("updateLocation", err);
        });
    } else {
      props.onConfirm(props.selectedLocation);
    }
  };

  // TODO: experimental marker cluster implementation
  const onClick_BACKUP_cluster = async (event: any) => {
    const feature = event.features[0];
    if (!feature) {
      return;
    }
    const clusterId = feature.properties.cluster_id;

    const geojsonSource: GeoJSONSource =
      mapRef.current.getSource("earthquakes");

    const zoom = await geojsonSource.getClusterExpansionZoom(clusterId);

    mapRef.current.easeTo({
      center: feature.geometry.coordinates,
      zoom,
      duration: 500,
    });
  };

  const computeFeatureBBox = (
    geometry: GeoJSON.Geometry
  ): [[number, number], [number, number]] => {
    const coords =
      geometry.type === "Polygon"
        ? geometry.coordinates.flat()
        : geometry.type === "MultiPolygon"
        ? geometry.coordinates.flat(2)
        : [];

    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;

    coords.forEach(([lng, lat]) => {
      minX = Math.min(minX, lng);
      maxX = Math.max(maxX, lng);
      minY = Math.min(minY, lat);
      maxY = Math.max(maxY, lat);
    });

    return [
      [minX, minY],
      [maxX, maxY],
    ];
  };

  const onClick = async (event: MapLayerMouseEvent) => {
    const map = mapRef.current?.getMap();
    if (!map) return;

    const features = map.queryRenderedFeatures(event.point, {
      layers: ["countries-layer-with-locations"],
    });

    if (!features.length) {
      setSelectedCountryCode(null);
      onSelectLocation(null);
      return;
    }

    const feature = features[0];
    const bbox = computeFeatureBBox(feature.geometry as GeoJSON.Geometry);

    map.fitBounds(bbox, {
      padding: 40,
      duration: 1000,
    });

    // TODO: show locations
    setSelectedCountryCode(feature.properties["ISO3166-1-Alpha-2"]);
    onSelectLocation(null);

    mapRef.current.setFeatureState(
      {
        source: "countries",
        id: feature.id,
      },
      { isSelected: true }
    );
  };

  /*const layerStyleSelectedCountry: LineLayerSpecification = {
    id: "countries-layer",
    type: "line",
    source: "line-source",
    layout: {
      "line-join": "round",
      "line-cap": "round",
    },
    paint: {
      "line-color": "dodgerblue",
      "line-width": 1,
      "line-opacity": [
        "case",
        ["boolean", ["feature-state", "isSelected"], false],
        1,
        0,
      ],
    },
  }*/

  const layerStyle: FillLayerSpecification = {
    id: "countries-layer",
    type: "fill",
    source: "countries",
    layout: {},
    paint: {
      "fill-color": "#6296eb",
      "fill-opacity": [
        "case",
        ["boolean", ["feature-state", "hover"], false],
        0.2,
        0,
      ],
    },
  };

  const layerStyleWithLocations: FillLayerSpecification = {
    id: "countries-layer-with-locations",
    type: "fill",
    source: "countries",
    layout: {},
    paint: {
      "fill-color": "dodgerblue",
      "fill-opacity": [
        "case",
        ["boolean", ["feature-state", "hasLocations"], false],
        0.5,
        0,
      ],
    },
  };

  const onMouseMove = (e: MapLayerMouseEvent) => {
    if (zoom > 4) {
      return;
    }
    if (!mapRef?.current) return;
    const features = mapRef.current.queryRenderedFeatures(e.point);

    //const featureId = features[0]?.id ?? features[0]?.properties?.name;
    const featureId = features[0]?.id;

    if (features.length > 0) {
      if (hoveredStateId) {
        mapRef.current.setFeatureState(
          {
            source: "countries",
            id: hoveredStateId,
          },
          { hover: false }
        );
      }
      //setHoveredStateId(featureId as string); // TODO: review
      setHoveredStateId((hoveredStateId) => {
        mapRef.current.setFeatureState(
          { source: "countries", id: hoveredStateId },
          { hover: false }
        );
        return featureId as any;
      });
      mapRef.current.setFeatureState(
        {
          source: "countries",
          id: featureId,
        },
        { hover: true }
      );
    }
  };

  const onMouseLeave = () => {
    if (zoom > 4) {
      return;
    }
    if (hoveredStateId) {
      mapRef.current.setFeatureState(
        { source: "countries", id: hoveredStateId },
        { hover: false }
      );
    }
    setHoveredStateId(null);
  };

  return (
    <Map
      id="mapData"
      onMove={onMapViewportChange}
      reuseMaps
      initialViewState={{
        longitude:
          defaultLocationLatLong != null
            ? defaultLocationLatLong[0]
            : locationState?.locations[0]?.longitude,
        latitude:
          defaultLocationLatLong != null
            ? defaultLocationLatLong[1]
            : locationState?.locations[0]?.latitude,
        zoom: 3,
      }}
      style={{ width: "100%", height: "100%" }}
      mapStyle={mapStyleJsons.openStreet}
      ref={mapRef}
      interactiveLayerIds={[clusterLayer.id, layerStyleWithLocations.id]}
      onZoom={(event: ViewStateChangeEvent) => {
        setZoom(event.viewState.zoom);
      }}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
      attributionControl={false}
      projection={props.projection ?? 'mercator'}
      onLoad={(event: MapLibreEvent) => {
        //event.target.resize();
      }}
    >
      <MapOverlayButton
        icon={isAddLocationMode ? <HiX size={20} /> : <HiPlus size={20} />}
        style={{
          pointerEvents: "all",
        }}
        onClick={() => {
          if (isAddLocationMode) {
            setIsAddLocationMode(false);
            setIsEditLocationMode(false);
          } else {
            setIsAddLocationMode(true);
          }
        }}
      />
      <GeolocateControl position="top-left" />
      {/* <FullscreenControl position="top-left" /> */}
      <NavigationControl position="top-left" />
      <ScaleControl position="bottom-left" />

      {/*<Source
        id="earthquakes"
        type="geojson"
        data={generateGeojsonFromLocations(locationState?.locations ?? [])}
        cluster={true}
        clusterMaxZoom={14}
        clusterRadius={50}
      >
        <Layer {...clusterLayer} />
        <Layer {...clusterCountLayer} />
        <Layer {...unclusteredPointLayer} />
      </Source>*/}

      <Source id="countries" type="geojson" data={countriesGeoJSON}>
        <Layer {...layerStyle} />
        <Layer {...layerStyleWithLocations} />
        {/*<Layer {...layerStyleSelectedCountry} />*/}
      </Source>

      {isAddLocationMode || isEditLocationMode ? (
        <Marker
          key={`marker-new-location`}
          draggable={false}
          onDrag={(e) => {
            //setNewLocationLatLong([e.lngLat.lat, e.lngLat.lng]);
          }}
          longitude={mapCenter[1]}
          latitude={mapCenter[0]}
          anchor="bottom"
          onClick={(e) => {
            // If we let the click event propagates to the map, it will immediately close the popup
            // with `closeOnClick: true`
            e.originalEvent.stopPropagation();
          }}
        >
          <Pin selected={true} />
        </Marker>
      ) : (
        <></>
      )}

      {pins}

      <MapOverlay
        visible={isAddLocationMode || isEditLocationMode}
        style={{
          pointerEvents: "all",
        }}
      >
        <LocationEditForm
          location={props.selectedLocation}
          onSubmit={onSubmit}
        ></LocationEditForm>
      </MapOverlay>

      <MapOverlay
        visible={
          !isMobile &&
          props.selectedLocation != null &&
          !isAddLocationMode &&
          !isEditLocationMode
        }
        style={{
          pointerEvents: "all",
        }}
      >
        <LocationInfo
          location={props.selectedLocation}
          onClick={props.onSelectLocationForDetail}
          rightIcon={<HiChevronRight />}
        ></LocationInfo>
      </MapOverlay>
    </Map>
  );
}
